/**
 * Text normalization pipeline dla SRO matcher.
 *
 * Pipeline: lowercase → strip Polish diacritics → split non-word → len>2 → strip stopwords.
 *
 * Asymmetric containment = |A∩B| / |A|  (bierze node jako referencję).
 * To ma sens dla "short node question vs long post" bo jeśli wszystkie tokeny noda
 * są w poście, containment = 1.0 niezależnie od długości posta.
 *
 * Uzywane w:
 *  - wpPostsSyncService.syncWpPostsWithContent / useWpPosts.syncWebsite (fill title_tokens_normalized + first_100_tokens_normalized)
 *  - useThematicNodes.createNodesBulk (fill canonical_question_tokens_normalized + query_frame)
 *  - existingPostMatcher Level 2 (containment scoring)
 */

import { POLISH_STOPWORDS_SET } from "./stopwords";

export type QueryFrame =
  | "definitional"
  | "procedural"
  | "comparative"
  | "causal"
  | "evaluative"
  | "mixed";

/**
 * Strip Polish diacritics: ą→a, ć→c, ę→e, ł→l, ń→n, ó→o, ś→s, ź→z, ż→z.
 * NFD decomposition + remove combining marks dla generic. + eksplicit dla ł/Ł (nie decompose).
 */
export function stripPolishDiacritics(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l")
    .replace(/Ł/g, "L");
}

/**
 * Normalize text to token array:
 *  1) lowercase
 *  2) strip Polish diacritics
 *  3) replace non-[a-z0-9\s\-] with space (zostawiamy myslniki dla compoundow typu "siem-fit")
 *  4) split whitespace
 *  5) filter length > 2
 *  6) filter out stopwords
 */
export function normalizeToTokens(text: string | null | undefined): string[] {
  if (!text || typeof text !== "string") return [];
  const stripped = stripPolishDiacritics(text.toLowerCase());
  return stripped
    .replace(/[^a-z0-9\s-]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 2 && !POLISH_STOPWORDS_SET.has(w));
}

export function normalizeToTokenSet(text: string | null | undefined): Set<string> {
  return new Set(normalizeToTokens(text));
}

/**
 * Asymmetric containment = |A∩B| / |A|.
 * Zwraca 0.0–1.0. 1.0 = wszystkie tokeny A sa w B.
 * Jesli A jest pusty → 0.
 */
export function asymmetricContainment(a: Set<string>, b: Set<string>): number {
  if (a.size === 0) return 0;
  let intersection = 0;
  for (const t of a) {
    if (b.has(t)) intersection++;
  }
  return intersection / a.size;
}

/**
 * Symmetric Jaccard (zachowane dla compat / fallback w dedupie).
 */
export function jaccardFromSets(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 && b.size === 0) return 0;
  let inter = 0;
  for (const t of a) if (b.has(t)) inter++;
  const uni = a.size + b.size - inter;
  return uni === 0 ? 0 : inter / uni;
}

/**
 * Heurystyka query_frame z canonical_question (bez LLM).
 * ~85% accuracy wg typologii SRO.
 *
 * Testy w kolejnosci (pierwszy match wygrywa):
 *  1) "co to jest" / "czym jest"  → definitional
 *  2) "jak ..."                   → procedural (chyba ze zawiera vs/lepsz → comparative)
 *  3) "vs" / "czy warto" / "roznic" / "porownan" → comparative
 *  4) "dlaczego" / "co powoduje"  → causal
 *  5) "najlepsz" / "top N" / "ranking" / "jakie/jaki wybrac" → evaluative
 *  6) default → mixed
 */
export function inferQueryFrameFromQuestion(question: string | null | undefined): QueryFrame {
  if (!question) return "mixed";
  const q = stripPolishDiacritics(question.toLowerCase()).trim();

  if (/^co\s+to\s+(jest|sa)\b/.test(q) || /^czym\s+(jest|sa)\b/.test(q)) return "definitional";
  if (/(vs\s|czy\s+(lepsz|warto|bardziej)|roznic|porownan|\s+a\s+\w+\?)/.test(q))
    return "comparative";
  if (/^dlaczego\b|^co\s+powoduje\b|z\s+powodu\b/.test(q)) return "causal";
  if (/najlepsz|top\s+\d+|najpopularn|najczesc|ranking|jakie?\s+wybra|jaki\s+wybra|ktor[ya]\s+wybra/.test(q))
    return "evaluative";
  if (/^jak\s/.test(q)) return "procedural";
  return "mixed";
}
